// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Dispatch } from 'redux';
import { deleteRequest, getJson, postJson } from 'Utils/http';
import { API_URL } from 'Config';
import deliveryEstimate from 'Utils/deliverables';
import { RootState } from 'Store';
import { getOrders } from 'Features/order/orderSelectors';
import { utilitiesDensities, defaultUtilitiesDensity } from 'Utils/constants';
import { DeleteCustomBundleAction, GetAllCustomBundlesAction, GetBundlesFailureAction, GetBundlesStartAction, GetBundlesSuccessAction, SelectedAdditionalDeliverablesAndLayersAction, SetAllBundlesAction, SetAttributeAddOnsFactorAction, SetAttributeAddOnsListAction, SetCatalogBrowseModeAction, SetCatalogTierDowngradedAction, SetCatalogLicenseFeeAction, SetDeliverablesAction, SetDeliveryDateAction, SetDeliveryDaysAction, SetIncludeAttributeAction, SetSurchargePolicyAction, SetUploadOwnDataAction, UpdateCustomBundleAction } from './bundleActions';

const bundleURLs = {
  standard: `${API_URL}/deliverables/getStandardDeliverables`,
  utilities: `${API_URL}/deliverables/getUtilitiesDeliverables`,
  custom: `${API_URL}/deliverables/getCustomDeliverables`,
};

// Utilities product catalog adapter
// The utilities checkout now reads from the catalog API (getProducts) instead of
// getUtilitiesDeliverables. Each product is mapped into the existing bundle shape so the
// existing utilities UI (BundleSelect radios, etc.) renders catalog data unchanged.
const productToBaseBundle = (product: ICatalogProduct): IBaseBundle => ({
  _id: product._id,
  name: product.name,
  version: 1,
  costPerFactors: [],
  timePerFactors: [],
  bundleId: product.bundleId,
  planID: '',
  subtype: product.subtype,
  dateAdded: '',
  acreAdjustmentFactor: [],
  deliverySpeedAdjustmentFactor: [],
});

// Map a catalog deliverable (default layer or available add-on) into the IDeliverable
// shape the existing utilities UI renders. type ('layer'|'deliverable') and subtype drive
// which section it lands in (included Layers, LayerSelect add-ons, DeliverableSelect).
const toBundleDeliverable = (deliverable: ICatalogDeliverable): IDeliverable => ({
  _id: deliverable._id,
  name: deliverable.name,
  version: 1,
  costPerFactors: [],
  timePerFactors: [],
  bundleId: 0,
  planID: '',
  dateAdded: '',
  bundleCost: 0,
  type: deliverable.type,
  subtype: deliverable.subtype ?? '',
  cost: 0,
  time: 0,
  requirements: [],
  dependencies: deliverable.dependencies ?? [],
  childDependencies: [],
  bundles: [],
  deliverableId: deliverable.deliverableId,
  available: true,
  selectable: true,
  selected: false,
  typeFactor: deliverable.typeFactor,
  attributeFactor: deliverable.attributeFactor,
  selectableValues: deliverable.selectableValues,
  multiSelect: deliverable.multiSelect,
  quoteOnly: deliverable.quoteOnly,
});

const productToBundle = (product: ICatalogProduct, dollarPerCredit: number, tierName: string | null, density: string): IBundle => ({
  _id: product._id,
  name: product.name,
  version: 1,
  costPerFactors: [],
  timePerFactors: [],
  bundleId: product.bundleId,
  planID: '',
  dateAdded: '',
  additionalDeliverables: product.additionalDeliverables.map(toBundleDeliverable),
  bundleDeliverables: product.defaultDeliverables.map(toBundleDeliverable),
  bundleCost: product.afterDiscountDollar ?? 0,
  priceCredit: product.afterDiscountCredit ?? 0,
  contactForPricing: product.afterDiscountDollar === null,
  priceBeforeDiscountDollar: product.totalBeforeDiscountDollar ?? 0,
  discountDollar: product.discountDollar ?? 0,
  // Per-product effective tier (its own size can earn above the org floor); fall back to the
  // top-level org tier for browse mode / older responses that don't carry per-product fields.
  dollarPerCredit: product.dollarPerCredit ?? dollarPerCredit,
  tierName: product.tierName ?? tierName,
  discountPercent: product.discountPercent,
  nextTierTeaser: product.nextTierTeaser ?? null,
  sizeBand: product.sizeBand ?? null,
  bufferMultiplier: product.bufferMultiplier ?? 1,
  deploymentFeeDollar: product.deploymentFeeDollar ?? 0,
  licenseFeeDollar: product.licenseFeeDollar ?? 0,
  // Recommended data provider + its after-discount price portion, so the Data step can show
  // the provider and the sidebar can drop the data cost when the customer uploads their own.
  dataProvider: product.dataProvider,
  dataProviderDollar: product.dataProviderDollar ?? 0,
  dataProviderCredit: product.dataProviderCredit ?? 0,
  // After-discount extraction-only charge; the sidebar shows this directly when the customer
  // uploads their own data, so the FE no longer subtracts the data portion itself.
  extractionDollar: product.extractionDollar ?? 0,
  extractionCredit: product.extractionCredit ?? 0,
  type: product.type,
  subtype: product.subtype,
  bundleDeliveryTime: 0,
  acreAdjustmentFactor: 0,
  adjustedOrderAcres: 0,
  // The catalog prices each product for a specific density tier; carry it so the UI shows
  // which density the displayed pricing reflects.
  density,
  deliverySpeed: 'normal',
  imageryCreditCost: 0,
  imageryDollarCost: 0,
  imageryExtraDollarCost: 0,
  deliverySpeedAdjustmentFactor: [],
});

// Map the catalog response into the bundle response shape, keeping only the products for
// the chosen subtype (Underground / Aerial / Landbase) — these become the radio options.
const mapProductsToBundleResponse = (data: IGetProductsResponse, subtype: string, density: string): IBundleResponse => {
  const subtypeProducts = data.products.filter((product) => product.subtype === subtype);
  return {
    deliverables: {
      bundles: subtypeProducts.map(productToBaseBundle),
      groupings: subtypeProducts.map((product) => productToBundle(product, data.dollarPerCredit, data.tierName, density)),
      densityOptions: utilitiesDensities,
      topoMajorOptions: [],
      topoMinorOptions: [],
      unit: '',
      attributeAddOnsList: [],
      attributeAddOnsFactor: 0,
    },
    message: '',
  };
};

export const GetBundlesThunk = (orderId: string, projectId: string, projectType: string, bundleSubtype: string, isEstimate = false, catalogDensity = defaultUtilitiesDensity, utilitiesCatalog = false, restoreSavedSelection = false) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const { token } = getState().auth;

    dispatch(GetBundlesStartAction());

    // The catalog experience is gated by the utilities-catalog LaunchDarkly flag, threaded in
    // from the calling component. An order that already is a catalog order keeps the catalog
    // path regardless of the flag; a fresh utilities form follows the flag, and flag-off falls
    // through to the legacy getUtilitiesDeliverables flow below.
    const { activeOrder: currentOrder } = getOrders(getState());
    const isCatalogOrder = currentOrder?.bundlesModel === 'order_product';
    // True when the restore branch found and re-applied a saved product; the form uses it
    // to land the customer at the end of the selection walk instead of replaying it.
    let restoredCatalogSelection = false;
    if (projectType === 'utilities' && (utilitiesCatalog || isCatalogOrder)) {
      // Density drives the per-linear-foot pricing tiers; linear feet + acreage come from
      // the project's active order server-side. The density picker re-runs this thunk to
      // re-price every product at the chosen tier. Restore callers pass the order's saved
      // density; anything the catalog doesn't price (e.g. a legacy 'normal') falls back to
      // the default so every product doesn't come back unpriceable.
      const effectiveDensity = utilitiesDensities.includes(catalogDensity) ? catalogDensity : defaultUtilitiesDensity;
      const catalogResult = await postJson<IGetProductsResponse>(`${API_URL}/catalog/getProducts`, { projectId, density: effectiveDensity }, token);
      if (catalogResult.success && catalogResult.data) {
        const response = mapProductsToBundleResponse(catalogResult.data, bundleSubtype, effectiveDensity);
        dispatch(GetBundlesSuccessAction(response));
        dispatch(SetAttributeAddOnsListAction([]));
        dispatch(SetAttributeAddOnsFactorAction(0));
        // The surcharge rule travels with the catalog so the sidebar can price layer toggles
        // locally; checkout recomputes what's actually charged from the order's selections.
        dispatch(SetSurchargePolicyAction(catalogResult.data.surchargePolicy ?? null));
        // Browse mode (no AOI yet): the server sends products with no pricing at all; the
        // form hides pricing and offers "Next: Define your area". Re-dispatched on every
        // fetch, so the flag drops the moment the project gains footage and re-prices.
        dispatch(SetCatalogBrowseModeAction(!!catalogResult.data.browseMode));
        // Account-level downgrade nudge: the account's standing tier is below the tier it last
        // ordered at. Re-dispatched every fetch so it tracks the server's current view.
        dispatch(SetCatalogTierDowngradedAction(!!catalogResult.data.tierDowngraded));
        // Account license-fee status for the sidebar's prepaid/waived label (committed orgs).
        dispatch(SetCatalogLicenseFeeAction({ prepaid: !!catalogResult.data.licenseFeePrepaid, cadence: catalogResult.data.licenseFeeCadence || 'none' }));
        const { groupings } = response.deliverables;
        const previousName = getState().bundle.selectedBundle?.name;
        if (isEstimate && (restoreSavedSelection || !previousName)) {
          // Opening onto a saved estimate: restore the saved selections. Callers that open
          // the form (or load the project page) pass restoreSavedSelection so the restore
          // runs even when a page-load warm-up already populated the selection — inferring
          // from empty state alone let that stale warm-up masquerade as in-session edits,
          // and the else branch's product re-select then wiped layers/add-ons/own-data.
          // The product is matched by name and never silently substituted — a renamed or
          // retired product leaves nothing selected so the customer chooses deliberately.
          // The saved add-ons carry their selectedValues, so the option pickers restore
          // too. A mid-edit density re-fetch passes false and skips this branch, keeping
          // in-session edits.
          const savedProduct = groupings.find((g) => g.name === currentOrder?.bundleName);
          if (savedProduct) {
            restoredCatalogSelection = true;
            dispatch(SetDeliverablesAction(savedProduct));
            (currentOrder.addOns ?? []).forEach((addOn) => {
              const additional = savedProduct.additionalDeliverables.find(
                (deliverable: IDeliverable) => deliverable._id === addOn.id || deliverable.name === addOn.name,
              );
              if (additional) {
                dispatch(SelectedAdditionalDeliverablesAndLayersAction({ ...additional, is3D: addOn.is3D, selectedValues: addOn.selectedValues }));
              }
            });
            const includeAttribute = (currentOrder.addOns ?? []).some((addOn) => addOn.attribute);
            dispatch(SetIncludeAttributeAction(includeAttribute));
            // After SetDeliverablesAction, which resets it: on restore the saved own-data
            // choice wins; a later product switch still resets it, as it should.
            dispatch(SetUploadOwnDataAction(!!currentOrder.orderProducts?.[0]?.uploadOwnData));
          }
        } else {
          // Keep the customer on the same product across a density re-fetch (re-priced); fall
          // back to the first product on the initial load / subtype change (no prior selection).
          const selectedProduct = groupings.find((g) => g.name === previousName) ?? groupings[0];
          if (selectedProduct) {
            dispatch(SetDeliverablesAction(selectedProduct));
          }
        }
      } else {
        dispatch(GetBundlesFailureAction());
      }
      return { deliverySpeed: 'normal', restoredCatalogSelection };
    }

    const url = bundleURLs[projectType as keyof typeof bundleURLs];
    const data = { orderId, projectId, subtype: bundleSubtype };
    const result = await postJson<IBundleResponse>(url, data, token);
    let bundleName = projectType === 'standard' ? '2D + Topo' : '';
    let density = 'normal';
    let deliverySpeed = 'normal';
    if (result.success) {
      dispatch(GetBundlesSuccessAction(result.data));
      const { attributeAddOnsList, attributeAddOnsFactor } = result.data.deliverables;
      dispatch(SetAttributeAddOnsListAction(attributeAddOnsList));
      dispatch(SetAttributeAddOnsFactorAction(attributeAddOnsFactor));
      const { activeOrder, placedOrder } = getOrders(getState());
      const { project } = getState().project;
      if (isEstimate) {
        bundleName = activeOrder.bundleName;
        density = activeOrder.density;
        deliverySpeed = activeOrder.deliverySpeed;
        const includeAttribute = activeOrder.addOns.some((addOn) => addOn.attribute);
        dispatch(SetIncludeAttributeAction(includeAttribute));
      }
      const bundles = result.data.deliverables.groupings;
      let findBundles;
      if (bundles.length === 1) {
        [findBundles] = bundles;
      } else if (placedOrder && project.qcLevel) {
        findBundles = bundles.find((b) => b.name === '2D Only - Express AI');
      } else if (bundleName?.length) {
        findBundles = bundles.find((b) => b.name === (bundleName) && b.density === (density || 'normal') && b.deliverySpeed === (deliverySpeed || 'normal'));
      } else {
        [findBundles] = bundles;
      }

      if (!findBundles) return {};
      const { additionalDeliverables, bundleDeliveryTime, bundleId } = findBundles;
      dispatch(SetDeliverablesAction(findBundles));

      if (isEstimate || findBundles.type === 'custom') {
        let deliveryTime = bundleDeliveryTime;
        let addOns;
        if (isEstimate) {
          addOns = activeOrder.addOns;
        } else {
          addOns = additionalDeliverables;
        }
        addOns.forEach((addOn) => {
          const additional = additionalDeliverables.find((deliverable: IDeliverable) => deliverable.name === addOn.name);
          if (additional) {
            deliveryTime += additional.time;
            dispatch(SelectedAdditionalDeliverablesAndLayersAction({ ...additional, is3D: addOn.is3D }));
          }
        });
        // If there are selected add-ons in A la carte bundle, add 2 days to the delivery time
        if (addOns.length > 0 && bundleId === 5) {
          if (deliverySpeed === 'expedited' || density === 'low') deliveryTime += 1.5;
          else if (deliverySpeed === 'super_expedited') deliveryTime += 1;
          else deliveryTime += 2;
        }
        dispatch(SetDeliveryDaysAction(deliveryTime));
        dispatch(SetDeliveryDateAction(deliveryEstimate(deliveryTime)));
      } else {
        dispatch(SetDeliveryDaysAction(bundleDeliveryTime));
        dispatch(SetDeliveryDateAction(deliveryEstimate(bundleDeliveryTime)));
      }
    } else {
      dispatch(GetBundlesFailureAction());
    }
    return {
      deliverySpeed,
    };
  };

export const GetCustomBundleThunk = (orderId: string, projectId: string) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const url = bundleURLs.custom;
    const { token } = getState().auth;

    const data = { orderId, projectId };
    const result = await postJson<IBundleResponse>(url, data, token);
    if (result.success) {
      const { attributeAddOnsList, attributeAddOnsFactor } = result.data.deliverables;
      dispatch(SetAttributeAddOnsListAction(attributeAddOnsList));
      dispatch(SetAttributeAddOnsFactorAction(attributeAddOnsFactor));
      return result.data;
    }
    return null;
  };

export const GetAllCustomBundlesThunk = (orgId: string) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const url = `${API_URL}/customBundle/${orgId}`;
    const { token } = getState().auth;

    dispatch(GetBundlesStartAction());
    const result = await getJson<ICustomBundleResponse>(url, token);
    if (result.success) {
      dispatch(GetAllCustomBundlesAction(result.data.customBundleList));
    }
  };

export const DeleteCustomBundleThunk = (bundleId: string) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const url = `${API_URL}/customBundle/${bundleId}`;
    const { token } = getState().auth;

    const result = await deleteRequest(url, token);
    if (result.success) {
      dispatch(DeleteCustomBundleAction(bundleId));
    }
  };

export const UpdateCustomBundleThunk = (_id: string, name: string, pricePerFoot?: number) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const url = `${API_URL}/customBundle/${_id}`;
    const { token } = getState().auth;
    const ownerOrganization = _ADMIN_ ? getState().admin?.orgId : getState().auth?.user?.ownerOrganization;
    const data = { name, ownerOrganization, ...(pricePerFoot !== undefined ? { pricePerFoot } : {}) };

    const result = await postJson<ICustomBundleResponse>(url, data, token);
    if (result.success && result.data.customBundle) {
      dispatch(UpdateCustomBundleAction(result.data.customBundle));
    }
    return result;
  };

export const SetDeliverablesThunk = (bundle: IBundle, keepAdditionals: boolean) => (dispatch: Dispatch, getState: () => RootState) => {
  const { selectedAdditionals } = getState().bundle;
  const { additionalDeliverables, bundleDeliveryTime, bundleId, deliverySpeed, density, includeAttribute } = bundle;

  dispatch(SetDeliverablesAction(bundle));
  // keepAdditionals boolean is true when density and delivery speed are changed, and false when bundle is changed
  if ((selectedAdditionals.length > 0 || (bundle.type === 'custom' && additionalDeliverables.length > 0)) && keepAdditionals) {
    let deliveryTime = bundleDeliveryTime;
    if (bundle.type === 'custom') {
      additionalDeliverables.forEach((addOn: IDeliverable) => {
        deliveryTime += addOn.time;
        dispatch(SelectedAdditionalDeliverablesAndLayersAction(addOn));
      });
    } else {
      selectedAdditionals.forEach((addOn) => {
        const additional = additionalDeliverables.find((deliverable: IDeliverable) => deliverable.name === addOn.name);
        deliveryTime += additional.time;
        dispatch(SelectedAdditionalDeliverablesAndLayersAction(additional));
      });
    }
    // If there are selectedAdditionals in A la carte bundle, add 2 days to the delivery time
    if (bundleId === 5) {
      if (deliverySpeed === 'expedited' || density === 'low') deliveryTime += 1.5;
      else if (deliverySpeed === 'super_expedited') deliveryTime += 1;
      else deliveryTime += 2;
    }
    dispatch(SetDeliveryDaysAction(deliveryTime));
    dispatch(SetDeliveryDateAction(deliveryEstimate(deliveryTime)));
  }
};

export const GetAllBundlesThunk = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const orgId = getState().admin.orgId || getState().account.org._id;
  const url = `${API_URL}/bundles/${orgId}`;
  const { token } = getState().auth;
  const result = await getJson<IAvailableBundles>(url, token);
  if (result.success) {
    dispatch(SetAllBundlesAction(result.data));
  }
};

export const GetDeliverablesByBundleThunk = (bundleName:string) => async (dispatch: Dispatch, getState: () => RootState): Promise<BundleData> => {
  const url = `${API_URL}/delverables/getDeliverablesByBundle`;
  const { token } = getState().auth;
  const data = { bundleName };
  const result = await postJson<BundleData>(url, data, token);
  return result.data;
};

export const CreateNewCustomBundleThunk = (customBundleName: string, addOns: {_id: string, is3D: boolean}[], deliverablesList: {_id: string, is3D: boolean}[], bundleId: string, includeAttribute: boolean) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const url = `${API_URL}/customBundle`;
    const { token } = getState().auth;
    const orgId = getState().admin.orgId || getState().account.org._id;
    const data = {
      name: customBundleName,
      ownerOrganization: orgId,
      bundleId,
      deliverablesList,
      addOns,
      includeAttribute,
    };
    const result = await postJson<{ customBundle: ICustomBundle }>(url, data, token);
    return result;
  };
